import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, SaveIcon, EyeIcon, Loader2Icon } from 'lucide-react';
import { CodeEditor } from '../components/CodeEditor';
import { PreviewModal } from '../components/PreviewModal';
import { FormPreview } from '../components/FormPreview';
import { GptCodeAssistant } from '../components/GptCodeAssistant';
import { FormBuilder } from '../components/builder/FormBuilder';
import { FormBlock, FormTheme, FormBuilderSchema, DEFAULT_FORM_THEME } from '../components/builder/types';
import { formTemplatesLibraryApi, FormTemplateRecord } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
const CATEGORIES = ['Newsletter', 'Contact', 'Event', 'Registration', 'Feedback', 'Other'];
export function FormTemplateLibraryEditor() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const isEditing = !!id && id !== 'new';
  const team = currentSubRegion || currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editorMode, setEditorMode] = useState<'code' | 'builder'>('code');
  const [activeTab, setActiveTab] = useState<'html' | 'css' | 'js'>('html');
  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [formData, setFormData] = useState({
    html: '',
    css: '',
    js: ''
  });
  const [builderBlocks, setBuilderBlocks] = useState<FormBlock[]>([]);
  const [builderTheme, setBuilderTheme] = useState<FormTheme>(DEFAULT_FORM_THEME);
  useEffect(() => {
    if (isEditing && id) {
      setIsLoading(true);
      formTemplatesLibraryApi.get(id).then(record => {
        if (record) {
          setTemplateName(record.Template_Name || '');
          setDescription(record.Description || '');
          setCategory(record.Category || '');
          setFormData({
            html: record.HTML || '',
            css: record.CSS || '',
            js: record.JavaScript1 || ''
          });
          if (record.Builder_Mode === 'builder' && record.Builder_Schema) {
            try {
              const schema: FormBuilderSchema = JSON.parse(record.Builder_Schema);
              setBuilderBlocks(schema.blocks || []);
              setBuilderTheme({
                ...DEFAULT_FORM_THEME,
                ...(schema.theme || {})
              });
              setEditorMode('builder');
            } catch {
              /* fallback to code */
            }
          } else if (record.Builder_Schema) {
            try {
              const schema: FormBuilderSchema = JSON.parse(record.Builder_Schema);
              setBuilderBlocks(schema.blocks || []);
              setBuilderTheme({
                ...DEFAULT_FORM_THEME,
                ...(schema.theme || {})
              });
            } catch {
              /* ignore */
            }
          }
        } else {
          toast.error('Template not found');
          navigate('/form-templates');
        }
      }).catch(err => {
        toast.error('Failed to load template', {
          description: err instanceof Error ? err.message : 'Unknown error'
        });
      }).finally(() => setIsLoading(false));
    }
  }, [id, isEditing, navigate]);
  const handleSave = async () => {
    if (!templateName.trim()) {
      toast.error('Template Name is required');
      return;
    }
    setIsSaving(true);
    const payload: Record<string, any> = {
      Template_Name: templateName,
      Description: description,
      Category: category,
      HTML: formData.html,
      CSS: formData.css,
      JavaScript1: formData.js,
      Builder_Mode: editorMode,
      Team: team
    };
    if (builderBlocks.length > 0 || editorMode === 'builder') {
      payload.Builder_Schema = JSON.stringify({
        blocks: builderBlocks,
        theme: builderTheme
      });
    }
    try {
      if (isEditing && id) {
        await formTemplatesLibraryApi.update(id, payload);
        toast.success('Form template updated');
      } else {
        await formTemplatesLibraryApi.create(payload);
        toast.success('Form template created');
        navigate('/form-templates');
      }
    } catch (err) {
      toast.error('Failed to save template', {
        description: err instanceof Error ? err.message : 'Unknown error'
      });
    } finally {
      setIsSaving(false);
    }
  };
  if (isLoading) {
    return <div className="flex items-center justify-center py-24" data-id="element-2917">
        <Loader2Icon className="h-8 w-8 text-blue-500 animate-spin" data-id="element-2918" />
        <span className="ml-3 text-sm text-gray-500" data-id="element-2919">Loading template...</span>
      </div>;
  }
  return <div className="space-y-6" data-id="element-2920">
      <div className="flex items-center justify-between" data-id="element-2921">
        <div className="flex items-center gap-4" data-id="element-2922">
          <Link to="/form-templates" className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-2923">
            <ArrowLeftIcon className="h-5 w-5" data-id="element-2924" />
          </Link>
          <div data-id="element-2925">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-2926">
              {isEditing ? 'Edit Form Template' : 'Create Form Template'}
            </h1>
            <p className="text-sm text-gray-500" data-id="element-2927">
              {editorMode === 'builder' ? 'Drag and drop blocks to build your template.' : 'Write HTML, CSS, and JS for this template.'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3" data-id="element-2928">
          <div className="inline-flex rounded-md border border-gray-300 bg-white p-0.5" data-id="element-2929">
            <button onClick={() => setEditorMode('code')} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${editorMode === 'code' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-2930">
              Code
            </button>
            <button onClick={() => {
            if (editorMode === 'code' && formData.html && builderBlocks.length === 0) {
              if (!confirm('Switching to Builder mode will start fresh. Continue?')) return;
            }
            setEditorMode('builder');
          }} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${editorMode === 'builder' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-2931">
              Builder
            </button>
          </div>
          <button onClick={() => setShowPreview(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-2932">
            <EyeIcon className="h-4 w-4" data-id="element-2933" /> Preview
          </button>
          <button onClick={handleSave} disabled={isSaving} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-2934">
            {isSaving ? <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-2935" /> : <SaveIcon className="h-4 w-4" data-id="element-2936" />}
            {isSaving ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </div>

      {editorMode === 'builder' ? <div className="space-y-6" data-id="element-2937">
          <FormBuilder initialBlocks={builderBlocks} initialTheme={builderTheme} onSchemaChange={(blocks, theme) => {
        setBuilderBlocks(blocks);
        setBuilderTheme(theme);
      }} onHtmlChange={(html, css) => {
        setFormData(prev => ({
          ...prev,
          html,
          css
        }));
      }} data-id="element-2938" />
          <div className="grid grid-cols-3 gap-6" data-id="element-2939">
            <div className="col-span-2" data-id="element-2940" />
            <SettingsPanel templateName={templateName} setTemplateName={setTemplateName} description={description} setDescription={setDescription} category={category} setCategory={setCategory} team={team} data-id="element-2941" />
          </div>
        </div> : <div className="grid grid-cols-3 gap-6" data-id="element-2942">
          <div className="col-span-2 space-y-6" data-id="element-2943">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm" data-id="element-2944">
              <div className="border-b border-gray-200 px-4 py-3 flex items-center gap-4" data-id="element-2945">
                {(['html', 'css', 'js'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`text-sm font-medium px-3 py-1.5 rounded-md transition-colors ${activeTab === tab ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-2946">
                    {tab === 'js' ? 'JavaScript' : tab.toUpperCase()}
                  </button>)}
              </div>
              <div className="p-4" data-id="element-2947">
                {activeTab === 'html' && <CodeEditor label="Form HTML" language="html" placeholder="<form>...</form>" value={formData.html} onChange={e => setFormData({
              ...formData,
              html: e.target.value
            })} data-id="element-2948" />}
                {activeTab === 'css' && <CodeEditor label="Custom CSS" language="css" placeholder=".form { ... }" value={formData.css} onChange={e => setFormData({
              ...formData,
              css: e.target.value
            })} data-id="element-2949" />}
                {activeTab === 'js' && <CodeEditor label="Custom JavaScript" language="javascript" placeholder="// Custom JS..." value={formData.js} onChange={e => setFormData({
              ...formData,
              js: e.target.value
            })} data-id="element-2950" />}
              </div>
            </div>
            <GptCodeAssistant mode="form" currentCode={formData} onCodeGenerated={code => {
          setFormData(prev => ({
            html: code.html || prev.html,
            css: code.css !== undefined ? code.css : prev.css,
            js: code.js !== undefined ? code.js : prev.js
          }));
        }} data-id="element-2951" />
          </div>
          <SettingsPanel templateName={templateName} setTemplateName={setTemplateName} description={description} setDescription={setDescription} category={category} setCategory={setCategory} team={team} data-id="element-2952" />
        </div>}

      <PreviewModal isOpen={showPreview} onClose={() => setShowPreview(false)} title="Form Template Preview" data-id="element-2953">
        <FormPreview html={formData.html} css={formData.css} data-id="element-2954" />
      </PreviewModal>
    </div>;
}
function SettingsPanel({
  templateName,
  setTemplateName,
  description,
  setDescription,
  category,
  setCategory,
  team
}: {
  templateName: string;
  setTemplateName: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  category: string;
  setCategory: (v: string) => void;
  team: string;
}) {
  return <div className="space-y-6" data-id="element-2955">
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4" data-id="element-2956">
        <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2" data-id="element-2957">
          Template Settings
        </h3>
        <div className="space-y-2" data-id="element-2958">
          <label className="block text-sm font-medium text-gray-700" data-id="element-2959">
            Template Name
          </label>
          <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="My Form Template" data-id="element-2960" />
        </div>
        <div className="space-y-2" data-id="element-2961">
          <label className="block text-sm font-medium text-gray-700" data-id="element-2962">
            Description
          </label>
          <textarea rows={3} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={description} onChange={e => setDescription(e.target.value)} placeholder="Describe what this template is for..." data-id="element-2963" />
        </div>
        <div className="space-y-2" data-id="element-2964">
          <label className="block text-sm font-medium text-gray-700" data-id="element-2965">
            Category
          </label>
          <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={category} onChange={e => setCategory(e.target.value)} data-id="element-2966">
            <option value="" data-id="element-2967">Select category...</option>
            {CATEGORIES.map(c => <option key={c} value={c} data-id="element-2968">
                {c}
              </option>)}
          </select>
        </div>
        <div className="space-y-2" data-id="element-2969">
          <label className="block text-sm font-medium text-gray-700" data-id="element-2970">
            Team
          </label>
          <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={team} disabled data-id="element-2971" />
        </div>
      </div>
    </div>;
}